import { ManifestEntry } from '../../types';
import { logger } from '../../utils/logger';
import type { AeroDocument } from '../../types/document-schema';
import type { DocumentOperation } from '../../types/document-operations';
import { api } from './base';

export const documentsApi = {
  list: async (filter?: { domain?: string; docType?: string }) => {
    const response = await api.get('/docs', { params: filter });
    return response.data.data as ManifestEntry[];
  },

  getByPath: async (path: string) => {
    const response = await api.get(`/docs/${path}`);
    return response.data.data;
  },

  loadAsMarkdown: async (projectId: string, documentNumber: string) => {
    // Load document HTML content from S3
    const response = await api.get(`/projects/${projectId}/documents/${documentNumber}`, {
      params: { includeContent: 'true' }
    });
    const doc = response.data.data;
    
    // Return content structure expected by editor
    return {
      content: doc.content || `<!DOCTYPE html>\n<html>\n<head><title>${documentNumber}</title></head>\n<body>\n<h1>${documentNumber}</h1>\n<p>Document content not available.</p>\n</body>\n</html>`,
      metadata: doc,
    };
  },

  saveContent: async (projectId: string, documentNumber: string, content: string, metadata: Record<string, unknown>) => {
    // Save document content
    const response = await api.patch(`/projects/${projectId}/documents/${documentNumber}`, {
      content,
      ...metadata,
    });
    return response.data.data;
  },
  
  // JSON Document Operations
  loadAsJson: async (projectId: string, documentNumber: string) => {
    // Load document as JSON structure
    logger.debug(`[documentsApi.loadAsJson] Loading ${documentNumber} from project ${projectId}`);
    const response = await api.get(`/projects/${projectId}/documents/${documentNumber}`, {
      params: { includeContent: 'true', format: 'json' }
    });
    logger.debug(`[documentsApi.loadAsJson] Response format:`, response.data.data?.format);
    logger.debug(`[documentsApi.loadAsJson] Has jsonDocument:`, !!response.data.data?.jsonDocument);
    logger.debug(`[documentsApi.loadAsJson] Has s3JsonKey:`, !!response.data.data?.s3JsonKey);
    logger.debug(`[documentsApi.loadAsJson] s3VersionId:`, response.data.data?.s3VersionId);
    return response.data.data.jsonDocument;
  },
  
  /**
   * Load document as JSON with metadata (including s3VersionId for undo, s3JsonKey for save path)
   */
  loadAsJsonWithMetadata: async (projectId: string, documentNumber: string): Promise<{ jsonDocument: AeroDocument | null; s3VersionId?: string; s3JsonKey?: string }> => {
    logger.debug(`[documentsApi.loadAsJsonWithMetadata] Loading ${documentNumber} from project ${projectId}`);
    const response = await api.get(`/projects/${projectId}/documents/${documentNumber}`, {
      params: { includeContent: 'true', format: 'json' }
    });
    const data = response.data.data;
    logger.debug(`[documentsApi.loadAsJsonWithMetadata] s3VersionId:`, data?.s3VersionId, 's3JsonKey:', data?.s3JsonKey);
    return {
      jsonDocument: data?.jsonDocument || null,
      s3VersionId: data?.s3VersionId,
      s3JsonKey: data?.s3JsonKey,
    };
  },
  
  applyOperations: async (projectId: string, documentNumber: string, operations: DocumentOperation[], baseVersion: string) => {
    // Apply structured operations to JSON document
    const response = await api.post(`/projects/${projectId}/documents/${documentNumber}/operations`, {
      operations,
      baseVersion
    });
    return response.data;
  },
  
  getOperationHistory: async (projectId: string, documentNumber: string) => {
    // Get operation history for audit trail
    const response = await api.get(`/projects/${projectId}/documents/${documentNumber}/operations/history`);
    return response.data;
  },
  
  /**
   * Save JSON document to S3 (full document save, not operations-based)
   * Used for metadata updates like plan targetDocument
   */
  saveJsonDocument: async (projectId: string, documentNumber: string, jsonDocument: AeroDocument) => {
    logger.debug(`[documentsApi.saveJsonDocument] Saving ${documentNumber} to project ${projectId}`);
    const response = await api.patch(`/projects/${projectId}/documents/${documentNumber}`, {
      jsonDocument,
    });
    return response.data.data;
  },
  
  // Version History Methods
  get: async (projectId: string, documentNumber: string, options?: { includeContent?: string; revision?: string }) => {
    // Get document with optional revision and content
    const response = await api.get(`/projects/${projectId}/documents/${documentNumber}`, {
      params: options || {}
    });
    return response.data.data;
  },
  
  getDownloadUrl: async (projectId: string, documentNumber: string, revision: string) => {
    // Get presigned download URL for specific revision
    const response = await api.get(`/projects/${projectId}/documents/${documentNumber}`, {
      params: { revision, download: 'true' }
    });
    return response.data.data.downloadUrl;
  },
  
  restoreVersion: async (projectId: string, documentNumber: string, revision: string) => {
    // Restore document to a previous revision (creates new draft)
    const response = await api.post(`/projects/${projectId}/documents/${documentNumber}/restore`, {
      sourceRevision: revision
    });
    return response.data.data;
  },
  
  /**
   * Prepare document for export (Word/DOCX)
   * Pre-renders all Mermaid diagrams as PNG images and returns the document with
   * _renderedPngBase64 fields populated for each diagram block.
   */
  prepareExport: async (projectId: string, documentNumber: string): Promise<AeroDocument> => {
    logger.debug(`[documentsApi.prepareExport] Preparing ${documentNumber} for export`);
    const response = await api.post(`/projects/${projectId}/documents/${documentNumber}/prepare-export`);
    // Response is wrapped in {success, data: {document}}
    return response.data.data.document;
  }
};

export const designDocumentsApi = {
  list: async (scope?: 'project-specific' | 'shared') => {
    const response = await api.get('/design-documents', { params: { scope } });
    return response.data.data;
  },

  get: async (documentNumber: string) => {
    const response = await api.get(`/design-documents/${documentNumber}`);
    return response.data.data;
  },

  create: async (designDoc: Record<string, unknown>) => {
    const response = await api.post('/design-documents', designDoc);
    return response.data.data;
  },

  linkToProject: async (documentNumber: string, projectId: string) => {
    const response = await api.post(`/design-documents/${documentNumber}/link/${projectId}`);
    return response.data.data;
  },

  unlinkFromProject: async (documentNumber: string, projectId: string) => {
    const response = await api.delete(`/design-documents/${documentNumber}/unlink/${projectId}`);
    return response.data.data;
  },

  createRevision: async (documentNumber: string, changeDescription: string, externalFileRevision?: string, ecoNumber?: string, author?: string) => {
    const response = await api.post(`/design-documents/${documentNumber}/revisions`, {
      changeDescription,
      externalFileRevision,
      ecoNumber,
      author
    });
    return response.data.data;
  }
};

export const documentLockApi = {
  /**
   * Acquire lock on a document
   */
  acquireLock: async (projectId: string, documentNumber: string) => {
    const response = await api.post(`/projects/${projectId}/documents/${documentNumber}/lock`);
    return response.data.data;
  },

  /**
   * Release lock on a document
   */
  releaseLock: async (projectId: string, documentNumber: string) => {
    const response = await api.delete(`/projects/${projectId}/documents/${documentNumber}/lock`);
    return response.data.data;
  },

  /**
   * Get lock status for a document
   */
  getLockStatus: async (projectId: string, documentNumber: string) => {
    const response = await api.get(`/projects/${projectId}/documents/${documentNumber}/lock`);
    return response.data.data;
  },
};

/**
 * Document Version History API - for undo/restore functionality
 */
export interface S3VersionInfo {
  versionId: string;
  lastModified: string;
  size: number;
  isLatest: boolean;
}

export interface VersionListResponse {
  documentNumber: string;
  s3Key: string;
  versions: S3VersionInfo[];
}

export const documentVersionApi = {
  /**
   * List S3 versions for a document
   */
  listVersions: async (projectId: string, documentNumber: string, limit: number = 20): Promise<VersionListResponse> => {
    const response = await api.get(`/projects/${projectId}/documents/${documentNumber}/versions`, {
      params: { limit },
    });
    return response.data.data;
  },

  /**
   * Restore a document to a previous S3 version
   */
  restoreVersion: async (projectId: string, documentNumber: string, versionId: string): Promise<{ message: string; restoredFromVersionId: string; newVersionId: string }> => {
    const response = await api.post(`/projects/${projectId}/documents/${documentNumber}/versions/${encodeURIComponent(versionId)}/restore`);
    return response.data.data;
  },

  /**
   * Fetch recent undo-able edit history for a document
   */
  undoHistory: async (projectId: string, documentNumber: string) => {
    const response = await api.get(`/projects/${projectId}/documents/${documentNumber}/undohistory`);
    return response.data.data;
  },
};
